import LookupDropdown from "components/blocks/LookupDropdown";
import PaginatedLookupDropdown from "components/blocks/PaginatedLookupDropdown";
import { Control, Controller, FieldErrors, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CreateOrderRequest, OrderLocalItemResult } from "services/orders/models/OrdersTypes";
import {ProgramByIDResponse, ProgramDetailsResult} from "../../../../../services/programs/models/ProgramTypes";
import { useEffect, useState } from "react";
import { BackendServices } from "services";
import Dropdown from "components/blocks/Dropdown";
import { useCookieContext } from "contexts";
import FormInput from "components/blocks/FormInput";

export default function LookupDropDowns({
    program,
    details,
  control,
  errors,
  setValue,
  order,
  isInspectMode,
    isReadonly
}: {
    program?:  ProgramByIDResponse | null;
    details?:  ProgramDetailsResult | null;
  control: Control<CreateOrderRequest, any>;
  errors: FieldErrors<CreateOrderRequest>;
  setValue: UseFormSetValue<CreateOrderRequest>;
  order?: OrderLocalItemResult | null;
  isInspectMode?: boolean;
  isReadonly?: boolean;
}) {
  const { t } = useTranslation(["Orders", "Common"]);
  const { isAr } = useCookieContext();
  const [researchPriorities, setResearchPriorities] = useState<any[]>([]);
  const [researchPapers, setResearchPapers] = useState<any[]>([]);
  const [collegeId, setCollegeId] = useState<number | undefined>(order?.collegeId);

  // Update collegeId when order data is loaded
  useEffect(() => {
    if (order?.collegeId || order?.researchPaper?.collegeId) {
      setCollegeId(order.collegeId || order.researchPaper?.collegeId);
    }
  }, [order?.collegeId, order?.researchPaper?.collegeId]);

  // Fetch and filter research priorities
  useEffect(() => {
    BackendServices.lookupService.getAllResearchPriorities().then((res) => {
      if (!res.hasError) {
        const filtered = res.data.filter(
          (item: any) => item.researchPriorityDetails && item.researchPriorityDetails.length > 0
        );
        const priorities = filtered.map((item: any) => ({
          id: String(item.id),
          value: item.researchPriorityDetails[0]?.displayName || "",
        }));

        // If editing and there's an existing research priority, ensure it's in the options
        if (order?.researchPriority && !priorities.find((p) => p.id === String(order.researchPriority?.id))) {
          priorities.unshift({
            id: String(order.researchPriority.id),
            value: order.researchPriority.displayName,
          });
        }

        setResearchPriorities(priorities);
      }
    });
  }, [isAr, order?.researchPriority?.id]);

  // Fetch and filter research papers
  useEffect(() => {
    if (!collegeId) {
      setResearchPapers([]);
      return;
    }
    BackendServices.lookupService.getAllResearchPapersByCollege({collegeId}).then((res) => {
      if (!res.hasError) {
        const filtered = res.data.filter(
          (item: any) => item.researchPaperDetails && item.researchPaperDetails.length > 0
        );
        const papers = filtered.map((item: any) => ({
          id: String(item.id),
          value: item.researchPaperDetails[0]?.displayName || "",
        }));

        // If editing and there's an existing research paper, ensure it's in the options
        if (order?.researchPaper && !papers.find((p) => p.id === String(order.researchPaperId))) {
          papers.unshift({
            id: String(order.researchPaper.id),
            value: order.researchPaper.displayName,
          });
        }

        setResearchPapers(papers);
      }
    });
  }, [collegeId, isAr, order?.researchPaper?.id]);

  return (
    <div className="d-flex flex-column gap-3">
        { program==null && <Controller
        name="centreManagementId"
        control={control}
        render={({ field }) => (
          <PaginatedLookupDropdown
            wrapperClassName="flex-1 "
            label={t("researchesCenter")}
            labelClassName="fw-bold"
            queryKey="search"
            service="orderService"
            endpoint="getManagementCentersDropdown"
            textValueKey="centreManagementDetail.displayName"
            idValueKey="id"
            
            withAsterisk
            isPaginated
            error={errors.centreManagementId?.message}
            value={field.value}
            disabled={!!isInspectMode || isReadonly}
            defaultOptions={
              order
                ? [{ value: `${order.centreManagementId}`, label: order.centreManagementDetail?.displayName ?? "" }]
                : null
            }
            onChange={(value: string) => {
              setValue("centreManagementId", +value);
            }}
          />
        )}
      />}   
        { program && !program.isCentreManagementHidden && <Controller
        name="centreManagementId"
        control={control}
        render={({ field }) => (
          <PaginatedLookupDropdown
            wrapperClassName="flex-1 "
            label={details?.centreManagementLabel??t("researchesCenter")}
            labelClassName="fw-bold"
            queryKey="search"
            service="orderService"
            endpoint="getManagementCentersDropdown"
            textValueKey="centreManagementDetail.displayName"
            idValueKey="id"
            withAsterisk={program?.isCentreManagementRequired ?? true}
            isPaginated
            error={errors.centreManagementId?.message}
            value={field.value}
            disabled={!!isInspectMode || isReadonly}
            defaultOptions={
              order
                ? [{ value: `${order.centreManagementId}`, label: order.centreManagementDetail?.displayName ?? "" }]
                : null
            }
            onChange={(value: string) => {
              setValue("centreManagementId", +value);
            }}
          />
        )}
      />}
      <div className="d-flex gap-3 ">
          { program==null &&    <Controller
          name="researchFieldId"
          control={control}
          render={({ field }) => (
            <LookupDropdown
              wrapperClassName="flex-1 "
              labelClassName="fw-bold"
              label={t("researchField")}
              service="lookupService"
              withAsterisk
              endpoint="getAllResearchFields"
              textValueKey="researchFieldDetail.displayName"
              idValueKey="id"
              noSelectionPlaceholder={t("researchField")}
              error={errors.researchFieldId?.message}
              disabled={isReadonly}
              value={field.value}
              onChange={(value: string) => {
                setValue("researchFieldId", +value);
              }}
              enableSearch
              useReactSelect
            />
          )}
        />
          }  { program && !program.isResearchFieldHidden &&    <Controller
          name="researchFieldId"
          control={control}
          render={({ field }) => (
            <LookupDropdown
              wrapperClassName="flex-1 "
              labelClassName="fw-bold"
              label={t("researchField")}
              service="lookupService"
              withAsterisk={program?.isCentreManagementRequired ?? true}
              endpoint="getAllResearchFields"
              textValueKey="researchFieldDetail.displayName"
              idValueKey="id"
              noSelectionPlaceholder={t("researchField")}
              error={errors.researchFieldId?.message}
              value={field.value}
              disabled={isReadonly}
              onChange={(value: string) => {
                setValue("researchFieldId", +value);
              }}
              enableSearch
              useReactSelect
            />
          )}
        />
          }
          { program==null &&    <Controller
          name="subspecialty"
          control={control}
          render={({ field }) => (
            <PaginatedLookupDropdown
              isCreatable
              isPaginated={false}
              wrapperClassName="flex-1 "
              labelClassName="fw-bold"
              label={t("subspecialty")}
              queryKey="search"
              service="lookupService"
              endpoint="getAllSubSpecialties"
              textValueKey="subspecialtyDetails.displayName"
              idValueKey="id"
              value={field.value}
              disabled={isReadonly}
              error={errors.subspecialty?.message}
              defaultOptions={order ? [{ value: order.subspecialty, label: order.subspecialty }] : null}
              onChange={(_: string, item) => {
                setValue("subspecialty", item.value);
              }}
            />
          )}
        />} 
          
          { program && !program.isResearchFieldHidden &&    <Controller
          name="subspecialty"
          control={control}
          render={({ field }) => (
            <PaginatedLookupDropdown
              isCreatable
              isPaginated={false}
              wrapperClassName="flex-1 "
              labelClassName="fw-bold"
              label={t("subspecialty")}
              queryKey="search"
              service="lookupService"
              endpoint="getAllSubSpecialties"
              disabled={isReadonly}
              textValueKey="subspecialtyDetails.displayName"
              idValueKey="id"
              value={field.value}
              error={errors.subspecialty?.message}
              defaultOptions={order ? [{ value: order.subspecialty, label: order.subspecialty }] : null}
              onChange={(_: string, item) => {
                setValue("subspecialty", item.value);
              }}
            />
          )}
        />}

      </div>

      {/* Research Priority and Search URL (DOI) on one line */}
      <div className="d-flex gap-3">
        {/* Research Priority */}
        <Controller
          name="researchPriorityId"
          control={control}
          render={({ field }) => (
            <Dropdown
              wrapperClassName="flex-1"
              labelClassName="fw-bold"
              label={t("researchPriority")}
              data={researchPriorities}
              noSelectionPlaceholder={t("selectResearchPriority")}
              error={errors.researchPriorityId?.message}
              disabled={isReadonly}
              value={field.value?.toString()}
              onChange={(value: string) => {
                setValue("researchPriorityId", value ? +value : undefined);
              }}
              useReactSelect
              withAsterisk
            />
          )}
        />

        {/* SearchURL (DOI) */}
        <Controller
          name="searchURL"
          control={control}
          render={({ field }) => (
            <FormInput
              wrapperClassName="flex-1"
              labelClassName="fw-bold"
              label={t("searchURL")}
              placeholder={t("enterSearchURL")}
              error={errors.searchURL?.message}
              disabled={isReadonly}
              value={field.value || ""}
              onChange={(e) => {
                setValue("searchURL", e.target.value || undefined);
              }}
              withAsterisk
            />
          )}
        />
      </div>

      {/* College and Research Paper on one line */}
      <div className="d-flex gap-3">
        {/* College for filtering Research Paper */}
        <Controller
          name="collegeId"
          control={control}
          render={({ field }) => (
            <LookupDropdown
              wrapperClassName="flex-1"
              labelClassName="fw-bold"
              label={t("college")}
              service="lookupService"
              endpoint="getAllCollegesDropDown"
              textValueKey="collegeDetail.displayName"
              idValueKey="id"
              noSelectionPlaceholder={t("selectCollege")}
              error={errors.collegeId?.message}
              disabled={isReadonly}
              value={field.value}
              onChange={(value: string) => {
                const newCollegeId = value ? +value : undefined;
                setValue("collegeId", newCollegeId);
                setCollegeId(newCollegeId);
                // Reset research paper when college changes
                setValue("researchPaperId", undefined);
              }}
              enableSearch
              useReactSelect
              withAsterisk
            />
          )}
        />

        {/* Research Paper - filtered by College */}
        <Controller
          name="researchPaperId"
          control={control}
          render={({ field }) => (
            <Dropdown
              key={`research-paper-${collegeId || 'all'}`}
              wrapperClassName="flex-1"
              labelClassName="fw-bold"
              label={t("researchPaper")}
              data={researchPapers}
              noSelectionPlaceholder={t("selectResearchPaper")}
              error={errors.researchPaperId?.message}
              disabled={isReadonly || !collegeId}
              value={field.value?.toString()}
              onChange={(value: string) => {
                setValue("researchPaperId", value ? +value : undefined);
              }}
              useReactSelect
              withAsterisk
            />
          )}
        />
      </div>
    </div>
  );
}
